import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Target, Loader2 } from 'lucide-react';

// Convert LAB values to approximate RGB (D65 illuminant) for preview
const labToRgb = (l, a, b) => {
  let y = (l + 16) / 116;
  let x = a / 500 + y;
  let z = y - b / 200;
  const cube = (t) => (t * t * t > 0.008856 ? t * t * t : (t - 16 / 116) / 7.787);
  x = 95.047 * cube(x);
  y = 100.0 * cube(y);
  z = 108.883 * cube(z);
  x /= 100; y /= 100; z /= 100;
  let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
  let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
  let bl = x * 0.0557 + y * -0.204 + z * 1.057;
  const gamma = (c) => (c > 0.0031308 ? 1.055 * Math.pow(c, 1 / 2.4) - 0.055 : 12.92 * c);
  r = Math.max(0, Math.min(1, gamma(r)));
  g = Math.max(0, Math.min(1, gamma(g)));
  bl = Math.max(0, Math.min(1, gamma(bl)));
  return [Math.round(r * 255), Math.round(g * 255), Math.round(bl * 255)];
};

const rgbToHex = (r, g, b) =>
  '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0').toUpperCase()).join('');

const drawLabWheel = (canvas, L) => {
  const size = 360;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(size, size);
  const R = size / 2;
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const dx = px - R;
      const dy = py - R;
      const idx = (py * size + px) * 4;
      if (Math.sqrt(dx * dx + dy * dy) > R) {
        img.data[idx + 3] = 0;
        continue;
      }
      const [rr, gg, bb] = labToRgb(L, (dx / R) * 128, -(dy / R) * 128);
      img.data[idx] = rr;
      img.data[idx + 1] = gg;
      img.data[idx + 2] = bb;
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
};

const LabReadout = ({ L, a, b, hex, onChangeL }) => (
  <div className="lg:col-span-5">
    <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3 inline-flex items-center gap-2">
      <Sparkles className="h-3.5 w-3.5" /> CIE L*a*b* color space
    </p>
    <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight leading-tight">
      The <span className="relative inline-block">
        <span className="relative z-10">true color</span>
        <span className="absolute left-0 right-0 -bottom-1 h-3 bg-[#FF4E50]/40 rounded-sm -z-0" />
      </span>{' '}
      playground
    </h2>
    <p className="mt-5 text-lg text-slate-600 leading-relaxed">
      Move your cursor across the wheel to sample colors in the CIE L*a*b* space — the exact same
      space our spectrophotometers measure in. Adjust <span className="font-bold text-[#0A2A63]">L (lightness)</span> with the slider.
    </p>

    {/* Readout */}
    <div className="mt-8 grid grid-cols-3 gap-3">
      <div className="rounded-2xl p-4 border border-slate-200 bg-white">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">L*</p>
        <p className="text-2xl font-black font-mono text-[#050F2E]">{Math.round(L)}</p>
      </div>
      <div className="rounded-2xl p-4 border border-slate-200 bg-white">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">a*</p>
        <p className="text-2xl font-black font-mono text-[#050F2E]">{a >= 0 ? '+' : ''}{Math.round(a)}</p>
      </div>
      <div className="rounded-2xl p-4 border border-slate-200 bg-white">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">b*</p>
        <p className="text-2xl font-black font-mono text-[#050F2E]">{b >= 0 ? '+' : ''}{Math.round(b)}</p>
      </div>
    </div>

    <div className="mt-5 flex items-center gap-4">
      <div className="h-14 w-14 rounded-2xl border-4 border-white shadow-lg" style={{ backgroundColor: hex }} />
      <div>
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">HEX</p>
        <p className="text-xl font-black font-mono text-[#050F2E]">{hex}</p>
      </div>
      <div className="ml-auto inline-flex items-center gap-1.5 bg-[#0A2A63] text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest">
        <Target className="h-3 w-3" /> ΔE &lt; 1.0
      </div>
    </div>

    {/* L slider */}
    <div className="mt-7">
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Lightness (L*)</label>
      <input
        type="range"
        min="5"
        max="95"
        value={L}
        onChange={(e) => onChangeL(Number(e.target.value))}
        className="mt-2 w-full accent-[#0A2A63] cursor-pointer"
      />
      <div className="flex justify-between text-[10px] text-slate-400 mt-1 font-mono">
        <span>5 (dark)</span>
        <span>50</span>
        <span>95 (light)</span>
      </div>
    </div>
  </div>
);

const LabColorWheel = () => {
  const [L, setL] = useState(65);
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  const [hover, setHover] = useState(false);
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Render the LAB wheel to a canvas whenever L changes
  useEffect(() => {
    if (!canvasRef.current) return;
    drawLabWheel(canvasRef.current, L);
    setReady(true);
  }, [L]);

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const dx = x - 0.5;
    const dy = y - 0.5;
    if (Math.sqrt(dx * dx + dy * dy) <= 0.5) {
      setPos({ x, y });
    }
  };

  const a = (pos.x - 0.5) * 2 * 128;
  const b = -(pos.y - 0.5) * 2 * 128;
  const hex = rgbToHex(...labToRgb(L, a, b));

  return (
    <section className="py-28 relative overflow-hidden bg-white" data-testid="lab-color-wheel-section">
      {/* Ambient background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 15% 30%, rgba(0,183,199,0.06), transparent 55%), radial-gradient(ellipse at 85% 70%, rgba(229,0,126,0.06), transparent 55%), radial-gradient(ellipse at 50% 100%, rgba(255,215,0,0.05), transparent 55%)',
        }}
      />

      <div className="container-pn relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <LabReadout L={L} a={a} b={b} hex={hex} onChangeL={setL} />

        {/* Wheel */}
        <div className="lg:col-span-7 relative flex justify-center">
          <div className="relative rounded-full shadow-2xl w-[min(86vw,460px)] aspect-square">
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-slate-100">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            )}

            <canvas
              ref={canvasRef}
              onMouseMove={onMove}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              className="w-full h-full rounded-full cursor-crosshair"
              style={{ display: 'block' }}
            />

            {/* Axis labels */}
            <div className="absolute inset-0 pointer-events-none">
              <span className="absolute top-2 left-1/2 -translate-x-1/2 text-[10px] font-mono text-white/80 font-bold">+b</span>
              <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] font-mono text-white/80 font-bold">−b</span>
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-white/80 font-bold">−a</span>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-white/80 font-bold">+a</span>
            </div>

            {/* Cursor pointer */}
            <div
              className="absolute pointer-events-none -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%` }}
            >
              <div className="relative">
                <div className="absolute inset-0 h-6 w-6 rounded-full ring-4 ring-white shadow-lg -translate-x-1/2 -translate-y-1/2" style={{ backgroundColor: hex }} />
                <div className={`absolute -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full border-2 border-white/70 ${hover ? 'animate-pulse-ring' : ''}`} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LabColorWheel;
